import React, { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { useStudents } from 'context/StudentsContext';
import { useIncidents } from 'context/IncidentsContext';
import { openPrintUrl } from '../../../../lib/openPrint';
import {
  ANTECEDENT_OPTIONS,
  BEHAVIOR_TYPE_OPTIONS,
  CONSEQUENCE_OPTIONS,
  FUNCTION_BY_VALUE,
  LOCATION_OPTIONS,
} from '../../../../lib/pbisConstants';
import {
  CATEGORICAL_PALETTE,
  CHART_COLORS,
  bucketByDay,
  bucketByMonth,
  buildPrintReportUrl,
  downloadCsv,
  formatDateRangeLabel,
  inRange,
  studentNameLookup,
} from '../../reportUtils';
import { KpiCard, ChartCard, EmptyState } from './IepProgressReport';

// Kebab-case enum → friendly label. The report is designed to be read
// by an IEP team (or printed and handed to a parent), so "Task / demand
// given" reads better than "task-demand". Values that aren't in the
// canonical option list fall back to a humanized version of the value
// so we never render "task-demand" as-is.
const buildLookup = (options) =>
  Object.fromEntries((options || []).map((o) => [o.value, o.label]));

const DETAIL_LOOKUPS = {
  behavior: buildLookup(BEHAVIOR_TYPE_OPTIONS),
  antecedent: buildLookup(ANTECEDENT_OPTIONS),
  consequence: buildLookup(CONSEQUENCE_OPTIONS),
  location: buildLookup(LOCATION_OPTIONS),
};

const humanizeEnum = (kind, value) => {
  if (!value) return '';
  return DETAIL_LOOKUPS[kind]?.[value] || String(value).replace(/-/g, ' ');
};

const formatIncidentDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatIncidentTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatDurationMmSs = (seconds) => {
  if (seconds == null) return '';
  const s = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m === 0) return `${rem}s`;
  if (rem === 0) return `${m}m`;
  return `${m}m ${rem}s`;
};

const intensityBadgeClasses = (intensity) => {
  if (!intensity) return 'bg-muted text-muted-foreground';
  const n = Number(intensity);
  if (n <= 2) return 'bg-success/10 text-success border border-success/30';
  if (n === 3) return 'bg-warning/10 text-warning border border-warning/30';
  return 'bg-destructive/10 text-destructive border border-destructive/30';
};

// ---------------------------------------------------------------------------
// Behavior Trends Report
//
// Rolls up behavior_incidents rows over the selected date range. Everything
// is time-based here (unlike IEP Progress, which is a snapshot):
//
//   * Incidents-per-day chart if the range is <= 60 days.
//   * Incidents-per-month chart if the range is longer (or unbounded).
//   * Distribution by intensity (1-5, from IncidentEntryForm).
//   * Distribution by behavior_type.
//   * Top students by count, so a case manager can see who's escalating.
// ---------------------------------------------------------------------------

const INTENSITY_LABEL = {
  1: '1 – Low',
  2: '2',
  3: '3 – Medium',
  4: '4',
  5: '5 – High',
};

const INTENSITY_COLOR = {
  1: CHART_COLORS.success,
  2: '#84CC16',
  3: CHART_COLORS.warning,
  4: '#F97316',
  5: CHART_COLORS.danger,
};

const BehaviorTrendsReport = ({ dateRange, studentFilter, printMode = false }) => {
  const { students } = useStudents();
  const { incidents, loading, error } = useIncidents();
  const animate = !printMode;
  const handlePrint = () => {
    const url = buildPrintReportUrl('behavior-trends', { dateRange, studentFilter });
    openPrintUrl(url);
  };

  const rollups = useMemo(() => {
    const nameFor = studentNameLookup(students);
    const activeStudents = (Array.isArray(students) ? students : []).filter(
      (s) => (s.status || 'active') !== 'archived',
    );
    const filterActive = studentFilter && studentFilter.size > 0;
    const inScopeStudents = filterActive
      ? activeStudents.filter((s) => studentFilter.has(s.id))
      : activeStudents;
    const inScopeStudentIds = new Set(inScopeStudents.map((s) => s.id));

    const scoped = (Array.isArray(incidents) ? incidents : []).filter((i) => {
      if (!inScopeStudentIds.has(i.studentId)) return false;
      if (!inRange(i.occurredAt, dateRange)) return false;
      return true;
    });

    // KPI numbers
    const totalIncidents = scoped.length;
    const highIntensity = scoped.filter((i) => Number(i.intensity) >= 4).length;
    const impactedStudents = new Set(scoped.map((i) => i.studentId)).size;

    const intensities = scoped
      .map((i) => Number(i.intensity))
      .filter((n) => Number.isFinite(n) && n > 0);
    const avgIntensity = intensities.length
      ? (intensities.reduce((a, b) => a + b, 0) / intensities.length).toFixed(1)
      : '—';

    // Trend chart: day-buckets when small range, month-buckets otherwise
    const trendRows = scoped.map((i) => ({
      occurredAt: (i.occurredAt || '').slice(0, 10),
    }));
    let trendData;
    let trendGranularity;
    if (dateRange?.from && dateRange?.to) {
      const days =
        (new Date(dateRange.to) - new Date(dateRange.from)) /
        (1000 * 60 * 60 * 24);
      if (days <= 60) {
        trendData = bucketByDay(trendRows, 'occurredAt', dateRange);
        trendGranularity = 'day';
      } else {
        trendData = bucketByMonth(trendRows, 'occurredAt', null, dateRange);
        trendGranularity = 'month';
      }
    } else {
      trendData = bucketByMonth(trendRows, 'occurredAt', null, dateRange);
      trendGranularity = 'month';
    }

    // Intensity distribution (1-5)
    const intensityMap = new Map();
    for (const i of scoped) {
      const n = Number(i.intensity);
      if (!Number.isFinite(n) || n < 1 || n > 5) continue;
      intensityMap.set(n, (intensityMap.get(n) || 0) + 1);
    }
    const byIntensity = [1, 2, 3, 4, 5].map((n) => ({
      name: INTENSITY_LABEL[n],
      value: intensityMap.get(n) || 0,
      color: INTENSITY_COLOR[n],
    }));

    // Behavior type distribution
    const typeMap = new Map();
    for (const i of scoped) {
      const key = (i.behaviorType || 'Unspecified').trim() || 'Unspecified';
      typeMap.set(key, (typeMap.get(key) || 0) + 1);
    }
    const byType = Array.from(typeMap.entries())
      .map(([name, value], idx) => ({
        name,
        value,
        color: CATEGORICAL_PALETTE[idx % CATEGORICAL_PALETTE.length],
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    // Per-student rollup (only those with at least 1 incident). While
    // we're walking `scoped` we ALSO collect the individual incident
    // rows per student so the detail section below can render the
    // actual behavior descriptions + notes without a second pass.
    const perStudentMap = new Map();
    for (const i of scoped) {
      const cur = perStudentMap.get(i.studentId) || {
        id: i.studentId,
        name: nameFor.get(i.studentId) || 'Unknown',
        count: 0,
        highIntensity: 0,
        intensitySum: 0,
        incidents: [],
        notesCount: 0,
      };
      cur.count += 1;
      if (Number(i.intensity) >= 4) cur.highIntensity += 1;
      const n = Number(i.intensity);
      if (Number.isFinite(n)) cur.intensitySum += n;
      cur.incidents.push(i);
      if ((i.notes && i.notes.trim().length > 0) ||
          (i.antecedentNotes && i.antecedentNotes.trim().length > 0)) {
        cur.notesCount += 1;
      }
      perStudentMap.set(i.studentId, cur);
    }
    const perStudent = Array.from(perStudentMap.values())
      .map((r) => ({
        ...r,
        avgIntensity: r.count
          ? (r.intensitySum / r.count).toFixed(1)
          : '—',
        // Show a student's incidents newest-first inside their group.
        incidents: [...r.incidents].sort((a, b) => {
          const ta = new Date(a.occurredAt || a.timestamp || 0).getTime();
          const tb = new Date(b.occurredAt || b.timestamp || 0).getTime();
          return tb - ta;
        }),
      }))
      .sort((a, b) => b.count - a.count);

    return {
      totalIncidents,
      highIntensity,
      impactedStudents,
      avgIntensity,
      trendData,
      trendGranularity,
      byIntensity,
      byType,
      perStudent,
      scopedRows: scoped,
    };
  }, [students, incidents, studentFilter, dateRange]);

  const handleExportCsv = () => {
    const filename = `behavior-trends-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCsv(
      filename,
      rollups.perStudent,
      [
        { key: 'name', label: 'Student' },
        { key: 'count', label: 'Incidents' },
        { key: 'highIntensity', label: 'High intensity (4-5)' },
        { key: 'avgIntensity', label: 'Avg intensity' },
      ],
    );
  };

  // ---- Per-incident detail section ----------------------------------------
  //
  // Interactive controls: expand/collapse per-student groups, filter to
  // only rows that carry a teacher note, and export the FULL detail
  // (student name + every ABC field + notes) to CSV. In print mode
  // everything is expanded, no controls render, and no state is used —
  // the page still renders correctly for autoprint.
  const [expandedStudentIds, setExpandedStudentIds] = useState(() => new Set());
  const [onlyWithNotes, setOnlyWithNotes] = useState(false);

  const toggleStudentExpanded = (id) => {
    setExpandedStudentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isStudentExpanded = (id) => printMode || expandedStudentIds.has(id);

  const allStudentsExpanded =
    rollups.perStudent.length > 0 &&
    rollups.perStudent.every((s) => expandedStudentIds.has(s.id));

  const expandAllStudents = () =>
    setExpandedStudentIds(new Set(rollups.perStudent.map((s) => s.id)));

  const collapseAllStudents = () => setExpandedStudentIds(new Set());

  const filterStudentIncidents = (list) => {
    if (!onlyWithNotes) return list;
    return list.filter(
      (i) =>
        (i.notes && i.notes.trim().length > 0) ||
        (i.antecedentNotes && i.antecedentNotes.trim().length > 0),
    );
  };

  const handleExportDetailCsv = () => {
    const filename = `behavior-incidents-detail-${new Date().toISOString().slice(0, 10)}.csv`;
    const rows = [];
    for (const student of rollups.perStudent) {
      for (const i of filterStudentIncidents(student.incidents)) {
        rows.push({
          student: student.name,
          date: formatIncidentDate(i.occurredAt),
          time: formatIncidentTime(i.occurredAt),
          behavior: humanizeEnum('behavior', i.behaviorType),
          behaviorDescription: i.behaviorDescription || '',
          location: humanizeEnum('location', i.location),
          antecedent: humanizeEnum('antecedent', i.antecedent),
          antecedentNotes: i.antecedentNotes || '',
          consequence: humanizeEnum('consequence', i.consequence),
          function:
            FUNCTION_BY_VALUE[i.hypothesizedFunction]?.label ||
            i.hypothesizedFunction ||
            '',
          intensity: i.intensity ?? '',
          duration: formatDurationMmSs(i.durationSeconds),
          followUp: i.followUpRequired ? 'Yes' : 'No',
          notes: i.notes || '',
        });
      }
    }
    downloadCsv(filename, rows, [
      { key: 'student', label: 'Student' },
      { key: 'date', label: 'Date' },
      { key: 'time', label: 'Time' },
      { key: 'behavior', label: 'Behavior type' },
      { key: 'behaviorDescription', label: 'Behavior description' },
      { key: 'location', label: 'Location' },
      { key: 'antecedent', label: 'Antecedent' },
      { key: 'antecedentNotes', label: 'Antecedent notes' },
      { key: 'consequence', label: 'Consequence' },
      { key: 'function', label: 'Function' },
      { key: 'intensity', label: 'Intensity (1-5)' },
      { key: 'duration', label: 'Duration' },
      { key: 'followUp', label: 'Follow-up' },
      { key: 'notes', label: 'Notes' },
    ]);
  };

  const detailRowCount = rollups.perStudent.reduce(
    (sum, s) => sum + filterStudentIncidents(s.incidents).length,
    0,
  );
  const studentsWithMatchingRows = rollups.perStudent.filter(
    (s) => filterStudentIncidents(s.incidents).length > 0,
  );
  const totalNotesCount = rollups.perStudent.reduce(
    (sum, s) => sum + s.notesCount,
    0,
  );

  if (error) {
    return (
      <div className="p-4 rounded-md bg-destructive/10 border border-destructive/30 text-sm text-foreground">
        <p className="font-medium">Couldn't load incidents</p>
        <p className="text-muted-foreground mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon="AlertTriangle"
          label="Total incidents"
          value={String(rollups.totalIncidents)}
          tone="warning"
          subtitle={formatDateRangeLabel(dateRange)}
        />
        <KpiCard
          icon="Flame"
          label="High intensity (4–5)"
          value={String(rollups.highIntensity)}
          tone="danger"
          subtitle={
            rollups.totalIncidents
              ? `${Math.round(
                  (rollups.highIntensity / rollups.totalIncidents) * 100,
                )}% of incidents`
              : 'None in range'
          }
        />
        <KpiCard
          icon="Users"
          label="Students impacted"
          value={String(rollups.impactedStudents)}
          tone="primary"
        />
        <KpiCard
          icon="Gauge"
          label="Avg intensity"
          value={String(rollups.avgIntensity)}
          tone="primary"
          subtitle="1 = low, 5 = high"
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Behavior trends
          </h2>
          <p className="text-sm text-muted-foreground">
            Every behavior incident logged in the selected window, rolled up
            and never mocked. Empty numbers just mean nothing was recorded in
            the range — that's a valid answer.
          </p>
        </div>
        {!printMode && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={handlePrint}
              iconName="Printer"
              iconPosition="left"
              title={
                rollups.totalIncidents === 0
                  ? 'Opens the print view — will show "no incidents in this window".'
                  : 'Open the print-ready view for this report'
              }
            >
              Print / Save as PDF
            </Button>
            <Button
              variant="outline"
              onClick={handleExportCsv}
              iconName="Download"
              iconPosition="left"
              disabled={rollups.perStudent.length === 0}
            >
              Export CSV
            </Button>
          </div>
        )}
      </div>

      {loading && rollups.totalIncidents === 0 && (
        <div className="p-4 rounded-md border border-border bg-muted/40 text-sm text-muted-foreground text-center">
          Loading incidents…
        </div>
      )}

      {rollups.totalIncidents === 0 && !loading ? (
        <EmptyState
          icon="AlertTriangle"
          title="No incidents in this range"
          body="Either your team is having a really good week, or you need to log the incidents that did happen. Switch the date range or head to Behavioral Incident Logging to add one."
        />
      ) : (
        <>
          <ChartCard
            title={
              rollups.trendGranularity === 'day'
                ? 'Incidents per day'
                : 'Incidents per month'
            }
          >
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={rollups.trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="label"
                  stroke="#6B7280"
                  fontSize={12}
                  interval="preserveStartEnd"
                />
                <YAxis stroke="#6B7280" fontSize={12} allowDecimals={false} />
                {!printMode && (
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                    }}
                  />
                )}
                <Bar
                  dataKey="value"
                  fill={CHART_COLORS.warning}
                  radius={[4, 4, 0, 0]}
                  isAnimationActive={animate}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="By intensity (1–5)">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={rollups.byIntensity}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="name" stroke="#6B7280" fontSize={12} />
                  <YAxis
                    stroke="#6B7280"
                    fontSize={12}
                    allowDecimals={false}
                  />
                  {!printMode && (
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                      }}
                    />
                  )}
                  <Bar
                    dataKey="value"
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={animate}
                    label={printMode ? { position: 'top', fontSize: 11, fill: '#111827' } : undefined}
                  >
                    {rollups.byIntensity.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="By behavior type (top 8)">
              {rollups.byType.length === 0 ? (
                <div className="h-[260px] flex items-center justify-center text-sm text-muted-foreground">
                  No behavior types recorded
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={rollups.byType}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, value }) => `${name}: ${value}`}
                      isAnimationActive={animate}
                    >
                      {rollups.byType.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    {!printMode && <Tooltip />}
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* Per-student totals -----------------------------------------
              Aggregate row per student (count / high-intensity / avg
              intensity). Each row is now expandable — clicking it drops
              a compact list of the student's individual incidents (date,
              intensity chip, behavior label, teacher notes) inline
              directly under the row, so a reader can go from "J Stafford:
              8 incidents, avg 3.0" to "here are those 8 incidents and
              what happened during each one" without scrolling to a
              separate section. Shares expand state with the more-detailed
              Incident detail (with notes) block below so the two never
              disagree about what's open. */}
          <div className="bg-surface border border-border rounded-lg overflow-hidden">
            <div className="p-4 border-b border-border flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
              <div>
                <h3 className="font-medium text-foreground">
                  Per-student totals
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Click a student to see each of their incidents inline —
                  date, intensity, and the teacher's notes for every one.
                </p>
              </div>
              {!printMode && rollups.perStudent.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName={
                    allStudentsExpanded ? 'ChevronsDownUp' : 'ChevronsUpDown'
                  }
                  iconPosition="left"
                  onClick={
                    allStudentsExpanded
                      ? collapseAllStudents
                      : expandAllStudents
                  }
                >
                  {allStudentsExpanded ? 'Collapse all' : 'Expand all'}
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    {!printMode && <th className="p-3 w-8" aria-hidden="true" />}
                    <th className="p-3">Student</th>
                    <th className="p-3">Incidents</th>
                    <th className="p-3">High intensity</th>
                    <th className="p-3">Avg intensity</th>
                    <th className="p-3">With notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rollups.perStudent.map((r) => {
                    const expanded = isStudentExpanded(r.id);
                    return (
                      <React.Fragment key={r.id}>
                        <tr
                          className={`hover:bg-muted/30 ${
                            !printMode ? 'cursor-pointer' : ''
                          } ${expanded ? 'bg-muted/20' : ''}`}
                          onClick={
                            printMode
                              ? undefined
                              : () => toggleStudentExpanded(r.id)
                          }
                          role={printMode ? undefined : 'button'}
                          tabIndex={printMode ? undefined : 0}
                          aria-expanded={printMode ? undefined : expanded}
                          onKeyDown={(e) => {
                            if (printMode) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleStudentExpanded(r.id);
                            }
                          }}
                        >
                          {!printMode && (
                            <td className="p-3 text-muted-foreground">
                              <Icon
                                name={expanded ? 'ChevronDown' : 'ChevronRight'}
                                size={16}
                              />
                            </td>
                          )}
                          <td className="p-3 font-medium text-foreground">
                            {r.name}
                          </td>
                          <td className="p-3 text-foreground">{r.count}</td>
                          <td className="p-3 text-foreground">
                            {r.highIntensity}
                          </td>
                          <td className="p-3 text-foreground">
                            {r.avgIntensity}
                          </td>
                          <td className="p-3 text-muted-foreground">
                            {r.notesCount || 0}
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="bg-muted/10">
                            <td
                              colSpan={printMode ? 5 : 6}
                              className="p-0"
                            >
                              <PerStudentInlineIncidents
                                incidents={r.incidents}
                                studentName={r.name}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per-incident detail (with notes) --------------------------------
              The per-student totals table above answers "who had how
              many incidents"; this section answers "what were those
              incidents, and what did the teacher write about them?" —
              which is what a reader actually needs to walk into a
              parent conference or an IEP team meeting. */}
          <IncidentDetailSection
            perStudent={rollups.perStudent}
            filterStudentIncidents={filterStudentIncidents}
            detailRowCount={detailRowCount}
            studentsWithMatchingRows={studentsWithMatchingRows}
            totalNotesCount={totalNotesCount}
            onlyWithNotes={onlyWithNotes}
            setOnlyWithNotes={setOnlyWithNotes}
            allStudentsExpanded={allStudentsExpanded}
            expandAllStudents={expandAllStudents}
            collapseAllStudents={collapseAllStudents}
            isStudentExpanded={isStudentExpanded}
            toggleStudentExpanded={toggleStudentExpanded}
            onExportCsv={handleExportDetailCsv}
            printMode={printMode}
          />
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// PerStudentInlineIncidents
//
// Compact per-incident list rendered inline under each row of the
// Per-student totals table. Purpose: the moment a reader sees
// "J Stafford: 8 incidents, avg 3.0" they can click and see what those
// 8 incidents were — date, intensity chip, behavior label, and the
// teacher's notes verbatim — without scrolling.
//
// This is intentionally lighter than IncidentDetailRow further down
// (which includes ABC data and duration) because it's meant to fit
// under an aggregate-totals row and read like "the story behind those
// numbers". The exhaustive per-incident view still lives in the
// Incident detail (with notes) block that follows.
// ---------------------------------------------------------------------------
const PerStudentInlineIncidents = ({ incidents, studentName }) => {
  if (!incidents || incidents.length === 0) {
    return (
      <div className="px-6 py-4 text-sm text-muted-foreground italic">
        No incidents in this range.
      </div>
    );
  }
  const notesCount = incidents.filter(
    (i) =>
      (i?.notes && i.notes.trim().length > 0) ||
      (i?.antecedentNotes && i.antecedentNotes.trim().length > 0),
  ).length;
  return (
    <div className="px-4 sm:px-6 py-4 space-y-3">
      <p className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">
          {incidents.length} incident{incidents.length === 1 ? '' : 's'}
        </span>{' '}
        for {studentName || 'this student'}
        {notesCount > 0 && (
          <>
            {' '}·{' '}
            <span className="font-medium">{notesCount}</span> with notes
          </>
        )}
        . Newest first.
      </p>
      <ol className="space-y-2">
        {incidents.map((incident, idx) => (
          <PerStudentInlineIncidentRow
            key={incident?.id || `${studentName}-${idx}`}
            incident={incident}
          />
        ))}
      </ol>
    </div>
  );
};

const PerStudentInlineIncidentRow = ({ incident }) => {
  const behaviorLabel = humanizeEnum('behavior', incident?.behaviorType);
  const locationLabel = humanizeEnum('location', incident?.location);
  const notes = (incident?.notes || '').trim();
  const antecedentNotes = (incident?.antecedentNotes || '').trim();
  const intensity = incident?.intensity;
  return (
    <li className="rounded-md border border-border bg-background p-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <span className="font-medium text-foreground">
          {formatIncidentDate(incident?.occurredAt)}
        </span>
        <span className="text-xs text-muted-foreground">
          {formatIncidentTime(incident?.occurredAt)}
        </span>
        {locationLabel && (
          <span className="text-xs text-muted-foreground">
            · {locationLabel}
          </span>
        )}
        <span
          className={`ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${intensityBadgeClasses(
            intensity,
          )}`}
        >
          Intensity {intensity ? `${intensity} / 5` : '—'}
        </span>
      </div>
      <div className="mt-1 text-sm font-semibold text-foreground">
        {behaviorLabel || 'Incident'}
        {incident?.followUpRequired && (
          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-warning/10 text-warning border border-warning/30">
            <Icon name="Flag" size={10} />
            Follow-up
          </span>
        )}
      </div>
      {notes ? (
        <div className="mt-2 rounded-md border-l-4 border-primary/60 bg-primary/5 px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-primary/80 mb-1">
            Teacher notes
          </p>
          <p className="text-sm text-foreground whitespace-pre-wrap">
            {notes}
          </p>
          {antecedentNotes && (
            <p className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">
              <span className="font-medium">Antecedent detail: </span>
              {antecedentNotes}
            </p>
          )}
        </div>
      ) : antecedentNotes ? (
        <p className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap">
          <span className="font-medium">Antecedent detail: </span>
          {antecedentNotes}
        </p>
      ) : (
        <p className="mt-2 text-xs italic text-muted-foreground">
          No teacher notes recorded.
        </p>
      )}
    </li>
  );
};

// ---------------------------------------------------------------------------
// IncidentDetailSection
//
// A per-student, expandable list of the individual incidents that back
// the graphs and totals above. Each student's rows sit under a
// collapsible header showing their name and incident count. The
// individual rows show every ABC field plus the teacher's notes
// verbatim — because the notes are the whole point of this section.
// ---------------------------------------------------------------------------
const IncidentDetailSection = ({
  perStudent,
  filterStudentIncidents,
  detailRowCount,
  studentsWithMatchingRows,
  totalNotesCount,
  onlyWithNotes,
  setOnlyWithNotes,
  allStudentsExpanded,
  expandAllStudents,
  collapseAllStudents,
  isStudentExpanded,
  toggleStudentExpanded,
  onExportCsv,
  printMode,
}) => (
  <div className="bg-surface border border-border rounded-lg overflow-hidden">
    <div className="p-4 border-b border-border flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h3 className="font-medium text-foreground flex items-center gap-2">
          <Icon name="ListChecks" size={16} className="text-primary" />
          Incident detail (with notes)
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Every incident behind the numbers above, grouped by student,
          with the ABC data and the teacher's notes shown verbatim.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Showing <span className="font-medium">{detailRowCount}</span>{' '}
          incident{detailRowCount === 1 ? '' : 's'} across{' '}
          <span className="font-medium">{studentsWithMatchingRows.length}</span>{' '}
          student{studentsWithMatchingRows.length === 1 ? '' : 's'}
          {totalNotesCount > 0 && (
            <>
              {' '}·{' '}
              <span className="font-medium">{totalNotesCount}</span> with notes
            </>
          )}
          .
        </p>
      </div>
      {!printMode && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 text-xs text-foreground select-none">
            <input
              type="checkbox"
              checked={onlyWithNotes}
              onChange={(e) => setOnlyWithNotes(Boolean(e.target.checked))}
              className="rounded border-border focus:ring-primary"
            />
            Only incidents with notes
          </label>
          <Button
            variant="outline"
            size="sm"
            iconName={allStudentsExpanded ? 'ChevronsDownUp' : 'ChevronsUpDown'}
            iconPosition="left"
            onClick={
              allStudentsExpanded ? collapseAllStudents : expandAllStudents
            }
            disabled={perStudent.length === 0}
          >
            {allStudentsExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
            onClick={onExportCsv}
            disabled={detailRowCount === 0}
          >
            Export detail CSV
          </Button>
        </div>
      )}
    </div>

    {detailRowCount === 0 ? (
      <div className="p-6 text-center text-sm text-muted-foreground">
        {onlyWithNotes
          ? 'No incidents in this range have notes attached.'
          : 'No incidents to show.'}
      </div>
    ) : (
      <div className="divide-y divide-border">
        {perStudent.map((student) => {
          const rows = filterStudentIncidents(student.incidents);
          if (rows.length === 0) return null;
          const expanded = isStudentExpanded(student.id);
          return (
            <StudentIncidentGroup
              key={student.id}
              student={student}
              rows={rows}
              expanded={expanded}
              onToggle={() => toggleStudentExpanded(student.id)}
              printMode={printMode}
            />
          );
        })}
      </div>
    )}
  </div>
);

const StudentIncidentGroup = ({
  student,
  rows,
  expanded,
  onToggle,
  printMode,
}) => {
  const notesCount = rows.filter(
    (i) =>
      (i.notes && i.notes.trim().length > 0) ||
      (i.antecedentNotes && i.antecedentNotes.trim().length > 0),
  ).length;
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        disabled={printMode}
        className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left ${
          printMode ? 'cursor-default' : 'hover:bg-muted/40'
        } transition-colors`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {!printMode && (
            <Icon
              name={expanded ? 'ChevronDown' : 'ChevronRight'}
              size={16}
              className="text-muted-foreground flex-shrink-0"
            />
          )}
          <div className="min-w-0">
            <p className="font-medium text-foreground truncate">
              {student.name}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {student.count} incident{student.count === 1 ? '' : 's'}
              {' · '}avg intensity {student.avgIntensity}
              {notesCount > 0 && ` · ${notesCount} with notes`}
              {student.highIntensity > 0 &&
                ` · ${student.highIntensity} high-intensity`}
            </p>
          </div>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-border bg-muted/20 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="p-3 whitespace-nowrap">When</th>
                <th className="p-3">Behavior</th>
                <th className="p-3">Where</th>
                <th className="p-3">A → C</th>
                <th className="p-3">Function</th>
                <th className="p-3 whitespace-nowrap">Intensity</th>
                <th className="p-3 whitespace-nowrap">Duration</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((incident) => (
                <IncidentDetailRow
                  key={incident.id || `${student.id}-${incident.occurredAt}`}
                  incident={incident}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Each incident renders as TWO stacked rows so the notes always get the
// full width of the table (not a squeezed 8th column). Row A is the
// scannable metadata line; Row B is a full-width notes row that appears
// beneath every incident — carrying the teacher's notes when present,
// or an italic "No teacher notes" placeholder when not, so the row
// count still matches the totals above.
const IncidentDetailRow = ({ incident }) => {
  const fn = incident?.hypothesizedFunction
    ? FUNCTION_BY_VALUE[incident.hypothesizedFunction]
    : null;
  const behaviorLabel = humanizeEnum('behavior', incident?.behaviorType);
  const antecedentLabel = humanizeEnum('antecedent', incident?.antecedent);
  const consequenceLabel = humanizeEnum('consequence', incident?.consequence);
  const locationLabel = humanizeEnum('location', incident?.location);
  const description = (incident?.behaviorDescription || '').trim();
  const antecedentNotes = (incident?.antecedentNotes || '').trim();
  const notes = (incident?.notes || '').trim();
  const duration = formatDurationMmSs(incident?.durationSeconds);
  return (
    <>
      <tr className="align-top border-b-0">
        <td className="p-3 whitespace-nowrap">
          <div className="font-medium text-foreground">
            {formatIncidentDate(incident?.occurredAt)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatIncidentTime(incident?.occurredAt)}
          </div>
        </td>
        <td className="p-3 min-w-40">
          <div className="text-foreground font-medium">{behaviorLabel || '—'}</div>
          {description && (
            <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">
              {description}
            </div>
          )}
        </td>
        <td className="p-3 min-w-24 text-foreground">{locationLabel || '—'}</td>
        <td className="p-3 min-w-40">
          <div className="text-xs">
            <span className="font-semibold text-muted-foreground mr-1">A:</span>
            <span className="text-foreground">{antecedentLabel || '—'}</span>
          </div>
          <div className="text-xs mt-1">
            <span className="font-semibold text-muted-foreground mr-1">C:</span>
            <span className="text-foreground">{consequenceLabel || '—'}</span>
          </div>
        </td>
        <td className="p-3 min-w-24">
          {fn ? (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border"
              style={{
                borderColor: fn.color,
                color: fn.color,
                backgroundColor: `${fn.color}14`,
              }}
            >
              {fn.label}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </td>
        <td className="p-3">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${intensityBadgeClasses(
              incident?.intensity,
            )}`}
          >
            {incident?.intensity ? `${incident.intensity} / 5` : '—'}
          </span>
        </td>
        <td className="p-3 text-foreground whitespace-nowrap">
          {duration || <span className="text-muted-foreground">—</span>}
        </td>
      </tr>
      <tr className="bg-background/60">
        <td className="pl-3 pr-3 pb-4 pt-0 align-top" />
        <td colSpan={6} className="pl-3 pr-4 pb-4 pt-0 align-top">
          {notes ? (
            <div className="rounded-md border border-border bg-background p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                Teacher notes
              </p>
              <p className="text-sm text-foreground whitespace-pre-wrap">
                {notes}
              </p>
              {antecedentNotes && (
                <p className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap">
                  <span className="font-medium">Antecedent detail: </span>
                  {antecedentNotes}
                </p>
              )}
              {incident?.followUpRequired && (
                <p className="mt-2 inline-flex items-center gap-1 text-xs text-warning font-medium">
                  <Icon name="Flag" size={12} />
                  Follow-up needed
                </p>
              )}
            </div>
          ) : antecedentNotes || incident?.followUpRequired ? (
            <div className="rounded-md border border-dashed border-border bg-background p-3">
              <p className="text-xs italic text-muted-foreground">
                No teacher notes recorded.
              </p>
              {antecedentNotes && (
                <p className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap">
                  <span className="font-medium">Antecedent detail: </span>
                  {antecedentNotes}
                </p>
              )}
              {incident?.followUpRequired && (
                <p className="mt-2 inline-flex items-center gap-1 text-xs text-warning font-medium">
                  <Icon name="Flag" size={12} />
                  Follow-up needed
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs italic text-muted-foreground">
              No teacher notes recorded.
            </p>
          )}
        </td>
      </tr>
    </>
  );
};

export default BehaviorTrendsReport;
